import React, { useState, useEffect, useCallback } from 'react';
import { PitchDetails } from '../../services/pitches';
import sessionsService from '../../services/sessions';
import { CollectionResult, Session } from '../../services/sessionModels';
import { SortingState, Sorting, PagingState, SelectionState, CustomPaging } from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableColumnResizing,
  PagingPanel,
  TableSelection,
} from '@devexpress/dx-react-grid-material-ui';
import useLocalStorage from '../Util/useLocalStorage';
import { withRouter, RouteComponentProps } from 'react-router';
import { sortval, played, downloadButton, created, homeTeam, awayTeam } from '../Sessions/Sessions';

interface Props extends RouteComponentProps {
  pitch: PitchDetails;
  selectedSession?: string;
  setSelectedSession: (session: string) => void;
}

const getRowId = (row: Session) => String(row.id);

const PitchSessions: React.FunctionComponent<Props> = (props) => {
  const [columnWidths, setColumnWidths] = useState<Array<{ columnName: string; width: number | string }>>([
    { columnName: 'id', width: 100 },
    { columnName: 'createdAt', width: 170 },
    { columnName: 'playedAt', width: 130 },
    { columnName: 'teamHome', width: 150 },
    { columnName: 'teamAway', width: 150 },
    { columnName: 'label', width: 150 },
    { columnName: 'download', width: 150 },
  ]);
  const [sorting, setSorting] = useLocalStorage<Sorting[]>('pitches-sort', [
    { columnName: 'playedAt', direction: 'asc' as 'asc' },
  ]);
  const [selection, setSelection] = useState(props.selectedSession ? [props.selectedSession] : []);
  const [page, setPage] = useLocalStorage<number>('pitch-sessions-page', 0);
  const [pageSize, setPageSize] = useLocalStorage<number>('pitch-sessions-pagesize', 10);

  useEffect(() => {
    if (props.selectedSession) {
      setSelection([props.selectedSession]);
    } else {
      setSelection([]);
    }
  }, [props.selectedSession]);

  const onSelectionChange = useCallback(
    (newselection: Array<string | number>) => {
      const lastSelected = newselection.find(
        (selected) => typeof selected === 'string' && selection.indexOf(selected) === -1
      );

      if (typeof lastSelected === 'string' && lastSelected !== undefined) {
        props.setSelectedSession(lastSelected);
      }
    },
    [props, selection]
  );

  let columns: any = [
    { name: 'id', title: 'ID', prio: 1 },
    { name: 'createdAt', title: 'Created', prio: 1, getCellValue: created },
    { name: 'playedAt', title: 'Date', prio: 1, getCellValue: played },
    { name: 'teamHome', title: 'Home team', getCellValue: homeTeam, prio: 2 },
    { name: 'teamAway', title: 'Away team', getCellValue: awayTeam, prio: 2 },
    { name: 'label', title: 'Label', prio: 2 },
    { name: 'download', title: ' ', getCellValue: downloadButton, prio: 1 },
  ];

  const [data, setData] = useState<CollectionResult<Session> | null>(null);
  const orderBy = sorting[0] ? sortval(sorting[0].columnName, columns) : undefined;
  const sortDescending = sorting[0]?.direction === 'desc';
  const pitchId = props.pitch.id;

  useEffect(() => {
    let cancelled = false;
    sessionsService
      .getSessions({
        offset: page * pageSize,
        limit: pageSize,
        orderBy,
        sortOrder: sortDescending ? 'Descending' : 'Ascending',
        pitchIds: [pitchId],
      })
      .then((result) => !cancelled && setData(result))
      .catch(() => !cancelled && setData(null));
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, orderBy, sortDescending, pitchId]);

  const { selectedSession, setSelectedSession } = props;

  useEffect(() => {
    if (!selectedSession && data?.items.length) {
      setSelectedSession(String(data.items[0].id));
    }
  }, [data, selectedSession, setSelectedSession]);

  useEffect(() => {
    if (data && Math.floor(data.total / pageSize) < page) {
      setPage(0);
    }
  }, [data, page, pageSize, setPage]);

  const count = data?.total || 0;

  return (
    <div>
      <h2>Sessions</h2>
      <Grid rows={data?.items || []} columns={columns} getRowId={getRowId}>
        <SortingState sorting={sorting} onSortingChange={setSorting} />
        <PagingState
          currentPage={page}
          onCurrentPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
        <SelectionState selection={selection} onSelectionChange={onSelectionChange} />
        <CustomPaging totalCount={count} />
        <Table rowComponent={(row) => <Table.Row {...row} hover />} />
        <TableColumnResizing columnWidths={columnWidths} onColumnWidthsChange={setColumnWidths} />
        <TableHeaderRow showSortingControls />
        <TableSelection selectByRowClick highlightRow showSelectionColumn={false} />
        <PagingPanel pageSizes={[10, 25, 50]} />
      </Grid>
    </div>
  );
};

export default withRouter(PitchSessions);
